<template>
  <div class="preorder-page">

    <!-- Page header -->
    <div class="page-header">
      <button class="back-btn" onclick="window.history.back()">
        <i class="ft-arrow-left"></i>
      </button>
      <div class="header-info">
        <span class="page-title">{{ patientName() }}</span>
        <span class="page-subtitle">
          <i class="ft-calendar"></i>
          {{ dayName }}, {{ date }}
        </span>
      </div>
    </div>

    <!-- Cart -->
    <div class="section-pad" v-if="cart.length > 0">
      <Cart show-delete="false" show-quantity="false" show-notes="false" show-checkout="true"></Cart>
    </div>

    <!-- Quick nav -->
    <div class="quick-nav">
      <router-link to="/" class="quick-nav-btn quick-nav-btn--ghost">
        <i class="ft-home"></i>
        <span>Home</span>
      </router-link>
      <router-link :to="nextPatientLink()" class="quick-nav-btn quick-nav-btn--primary">
        <i class="ft-users"></i>
        <span>Next Patient</span>
      </router-link>
    </div>

    <!-- Period cards -->
    <div class="periods-section">
      <p class="section-label">
        <i class="ft-grid"></i> Meal Periods
      </p>

      <div
        class="period-card"
        v-for="period in periods"
        :key="period.id"
        :class="{ 'period-card--closed': cutOffReached(period, period.id, period.open_time, period.close_time) }"
      >

        <!-- Period header -->
        <div class="period-header">
          <div class="period-title-row">
            <div class="period-name-group">
              <span class="period-icon-wrap" v-html="periodIconSvg(period)"></span>
              <span class="period-name">{{ period.title }}</span>
            </div>
            <span
              class="period-status-badge"
              :class="cutOffReached(period, period.id, period.open_time, period.close_time) ? 'badge--closed' : 'badge--open'"
            >
              <i :class="cutOffReached(period, period.id, period.open_time, period.close_time) ? 'ft-lock' : 'ft-unlock'"></i>
              {{ cutOffReached(period, period.id, period.open_time, period.close_time) ? 'Closed' : 'Open' }}
            </span>
          </div>

          <div class="period-meta" v-if="period.cut_off || period.open_time">
            <span class="meta-chip" v-if="period.cut_off">
              <i class="ft-alert-circle"></i> Cut-off {{ formatTime(period.cut_off) }}
            </span>
            <span class="meta-chip" v-if="period.open_time && period.close_time">
              <i class="ft-clock"></i> {{ formatTime(period.open_time) }} – {{ formatTime(period.close_time) }}
            </span>
          </div>
          <div class="period-meta" v-else>
            <span class="meta-chip meta-chip--muted">
              <i class="ft-clock"></i> No time restriction
            </span>
          </div>
        </div>

        <!-- Meal items -->
        <div class="meal-list">
          <template v-for="order in orders">
            <template v-if="showOrderRow(order, period)">

              <!-- Order status badge -->
              <div class="order-status-row" :class="order.status == '1' ? 'order-status--pending' : 'order-status--confirmed'">
                <span class="order-status-dot"></span>
                <span class="order-status-label">{{ order.status == '1' ? 'New' : 'Confirmed' }}</span>
                <span class="order-status-hint" v-if="order.status != '1'">This order has been confirmed and cannot be edited.</span>
              </div>

              <!-- Item rows -->
              <div
                v-for="(item, index) in order.items"
                :key="order.id + '-' + index"
                class="meal-row"
              >
                <span class="meal-dot"></span>
                <span class="meal-name">{{ item.label }}</span>

                <!-- Inline quantity + remove (only for pending, non-NPM items) -->
                <div
                  class="meal-row-controls"
                  v-if="item.label != 'NIL PER MOUTH' && item.id != '794570' && order.status == '1' && !cutOffReached(period, period.id, period.open_time, period.close_time)"
              >
                  <button class="remove-btn" @click.stop="removeOrder(order, index)" title="Remove item">
                    <i class="ft-x"></i>
                  </button>
                </div>

                <!-- Quantity display only (confirmed orders) -->
                <span class="qty-readonly" v-else>× {{ item.quantity || 1 }}</span>
              </div>

              <!-- Note row -->
              <div class="note-row">
                <div class="note-icon-wrap"><i class="ft-message-square"></i></div>
                <span class="note-text">{{ order.note || 'No dietary note' }}</span>
                <button class="edit-note-btn" @click.stop="editNote(order)" title="Edit note" v-if="order.status == '1'">
                  <i class="ft-edit-2"></i>
                </button>
              </div>

            </template>
          </template>
        </div>

        <!-- Empty state -->
        <div class="empty-state" v-if="!hasOrdersForPeriod(period)">
          <i class="ft-inbox"></i>
          <span>No meals ordered yet</span>
        </div>

        <!-- Add meal -->
        <button
          class="add-meal-btn"
          @click="mealCategories(period, period.id, period.open_time, period.close_time)"
          :disabled="cutOffReached(period, period.id, period.open_time, period.close_time)"
          :class="{ 'add-meal-btn--disabled': cutOffReached(period, period.id, period.open_time, period.close_time) }"
        >
          <i :class="cutOffReached(period, period.id, period.open_time, period.close_time) ? 'ft-lock' : 'ft-plus-circle'"></i>
          {{ cutOffReached(period, period.id, period.open_time, period.close_time) ? 'Period Closed' : 'Add Meal' }}
        </button>

      </div>
    </div>

    <!-- Note modal -->
    <div class="modal" tabindex="-1" role="dialog" id="note-modal">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title"><i class="ft-message-square"></i> Dietary Note</h5>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <fieldset class="form-group mb-0">
              <label class="note-label">Note for this meal</label>
              <textarea class="form-control note-textarea" v-model="note" placeholder="e.g. No onions, diabetic diet…"></textarea>
            </fieldset>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn-cancel-note" data-dismiss="modal">Cancel</button>
            <button type="button" class="btn-save-note" v-on:click="saveNote(order)" data-dismiss="modal">
              <i class="ft-check"></i> Save Note
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import moment from 'moment'
import swal from 'sweetalert'
import Cart from '@/components/navigation/Cart.vue'

export default {
  name: 'preorder',
  components: {
    Cart
  },
  data: function () {
    return {
      periods: [],
      orders: [],
      dayName: this.getDayName(),
      date: this.getDate(),
      today: this.toDay(),
      now: this.nowTime(),
      clean_date: this.$route.params.date,
      note:"",
      order:{
        id:0,
        note:""
      },
      cart: window.localStorage.getItem('cart') ? JSON.parse(window.localStorage.getItem('cart')) : []
    }
  },
  mounted: function () {
    this.$store.commit('SET_LAYOUT', 'AppLayout')

    var that = this
    var user = JSON.parse(window.localStorage.getItem('user'))
    var merchantId = user.merchant.id
    var bedId = this.$route.params.bed
    var url = this.$config.apiUrl +'medicater/calendar/?merchant_id=' + merchantId + '&bed_id=' + bedId
    if(window.localStorage.getItem('free_issue') == "Y"){
      var ward = JSON.parse(window.localStorage.getItem('ward'))
      url = this.$config.apiUrl +'canteenapp_basic/calendar_free_issues?ward_id='+ward.ward_id+'&merchant_id=' + merchantId + '&bed_id=' + bedId
    }
    $.ajax({
      beforeSend: function (xhr) {
        xhr.setRequestHeader('Authorization', window.localStorage.getItem('token'))
      },
      url: url,
      type: 'get',
      success: function (data) {
        var periods = []
        if (data.success === true) {
          data.data.periods_new.forEach(function (period) {
            periods.push({
              id: period.id,
              title: period.name,
              open_time: period.period_open_time,
              close_time: period.period_close_time,
              cut_off: period.period_cut_off,
              served: "("+period.period_open_time+"-"+period.period_close_time+")"
            })
          })
        }
        that.periods = periods
        var orders = []
        data.data.calendar.forEach(function (day) {
          if (day.orders) {
            that.periods.forEach(function (period) {
              for (var key in day.orders[period.id]) {
                orders.push(day.orders[period.id][key])
              }
            })
          }
        })
        that.orders = orders
      },
      error: function (data) {
        if (window.localStorage.getItem(this.url)) {
          var offlineData = JSON.parse(window.localStorage.getItem(this.url))
          this.success(offlineData)
        } else {
          swal({
            text: 'Please connect to the internet',
            icon: 'info',
            button: 'Ok'
          })
        }
      }
    })
  },
  methods: {
    formatTime: function (timeStr) {
      if (!timeStr) return ''
      return moment(timeStr, 'HH:mm:ss').format('h:mm A')
    },
    periodIconSvg: function (period) {
      var name = (period.title || '').toLowerCase()
      var a = 'xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"'
      if (name.includes('breakfast')) {
        return '<svg ' + a + '><path d="M17 18a5 5 0 0 0-10 0"/><line x1="12" y1="2" x2="12" y2="9"/><line x1="4.22" y1="10.22" x2="5.64" y2="11.64"/><line x1="1" y1="18" x2="3" y2="18"/><line x1="21" y1="18" x2="23" y2="18"/><line x1="18.36" y1="11.64" x2="19.78" y2="10.22"/><line x1="23" y1="22" x2="1" y2="22"/><polyline points="8 6 12 2 16 6"/></svg>'
      }
      if (name.includes('lunch')) {
        return '<svg ' + a + '><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>'
      }
      if (name.includes('dinner')) {
        return '<svg ' + a + '><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>'
      }
      return '<svg ' + a + '><path d="M18 8h1a4 4 0 0 1 0 8h-1"/><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg>'
    },
    hasOrdersForPeriod: function (period) {
      return this.orders.some(order => this.showOrderRow(order, period))
    },
    printedOrder: function (orders, period_id){
      if(orders == undefined || orders == '' || orders == null){
        return false
      }
      var printed = false
      for (var key in orders) {
        if(orders[key].status != '1' && orders[key].period_id === period_id && this.$route.params.date === orders[key].date){
          printed = true
        }
      }
      return printed
    },
    removeOrderNotAllowed: function (){
      swal('Error', "This order has already been printed for this period")
    },
    addOrderNotAllowed: function (order, period_id){
      var that = this
      if(that.printedOrder(order, period_id)){
        swal('Error', "An order has already been place and printed for this period")
      }
      return false
    },
    nowTime: function(){
      var today = new Date();
      return today.getHours() + ":" + today.getMinutes();
    },
    toDay: function(){
      var today = new Date();
      return today.getFullYear()+'-'+(today.getMonth()+1)+'-'+today.getDate()+" "+this.nowTime();
    },
    nextPatientLink: function () {
      var bed = JSON.parse(window.localStorage.getItem('bed'))
      return '/unconfirmed?ward=' + bed.ward_id
    },
    patientName: function () {
      var bed = JSON.parse(window.localStorage.getItem('bed'))
      return bed.patient_name
    },
    getDayName: function () {
      return moment(this.$route.params.date).format('dddd')
    },
    getDate: function () {
      return moment(this.$route.params.date).format('LL')
    },
    mealCategories: function (period, periodId, openTime, closeTime) {
      if(window.localStorage.getItem('nil_per_mouth_periods') != undefined && window.localStorage.getItem('nil_per_mouth_periods') != ''){
        var nil_periods = JSON.parse(window.localStorage.getItem('nil_per_mouth_periods'))
        for (var key in nil_periods) {
          if(nil_periods[key] == period.id){
            swal('Error', "A nil per mouth order has been placed for this period, no further order required")
            return false
          }
        }
      }

      if (this.$route.params.date === moment().format('YYYY-MM-DD')) {
        var currentUnix = moment().format('X')
        var endUnix = moment(this.$route.params.date + ' ' + closeTime).format('X')
        if (currentUnix > endUnix) {
          swal('Error', 'You are unable to place an order for this period due to the cut off time being reached. Please select a different period or date.', 'error')
          return false
        }
      }

      localStorage.setItem('period', JSON.stringify(period))
      this.$router.push('/preorder/' + this.$route.params.ward + '/' + this.$route.params.bed + '/' + this.$route.params.date + '/' + period.id + '/categories')
    },
    cutOffReached: function (period, periodId, openTime, closeTime){
      var returnVal = false
      if(this.$route.params.date === moment().format('YYYY-MM-DD')) {
        var currentUnix = moment().format('X')
        var endUnix = moment(this.$route.params.date + ' ' + closeTime).format('X')
        if (currentUnix > endUnix) {
          returnVal = true
        }else {
          returnVal = false
        }
      }
      return returnVal
    },
    showOrderRow: function (order, period) {
      if (this.$route.params.date === order.date && order.period_id === period.id) {
        return true
      }
      return false
    },
    removeOrder: function (order, itemIndex) {
      var that = this
      var mealId = order.items[itemIndex].id

      // RE-01 fix: update Vue data reactively — never touch the DOM directly
      order.items.splice(itemIndex, 1)
      if (order.items.length === 0) {
        var idx = that.orders.indexOf(order)
        if (idx > -1) that.orders.splice(idx, 1)
      }

      $.ajax({
        beforeSend: function (xhr) {
          xhr.setRequestHeader('Authorization', window.localStorage.getItem('token'))
        },
        url: that.$config.apiUrl + 'canteenapp_orders/remove?item_id=' + mealId,
        type: 'delete',
        success: function (data) {
          var user = JSON.parse(window.localStorage.getItem('user'))
          user.wallet_balance = data.data.wallet_balance
          window.localStorage.setItem('user', JSON.stringify(user))
        },
        error: function () {
          swal({ text: 'You are currently offline. Your order will be deleted when you reconnect.', icon: 'info', button: 'Ok' })
          var queue = window.localStorage.getItem('queue') ? JSON.parse(window.localStorage.getItem('queue')) : []
          queue.push({ url: that.$config.apiUrl + 'canteenapp_orders/remove?item_id=' + mealId, data: {}, method: 'delete' })
          window.localStorage.setItem('queue', JSON.stringify(queue))
        }
      })
    },
    adjustQty: function (order, itemIndex, delta) {
      var that = this
      var item = order.items[itemIndex]
      var currentQty = Number(item.quantity) || 1
      var newQty = currentQty + delta
      if (newQty < 1) return

      // Update Vue data reactively
      that.$set(order.items, itemIndex, Object.assign({}, item, { quantity: newQty }))

      $.ajax({
        beforeSend: function (xhr) {
          xhr.setRequestHeader('Authorization', window.localStorage.getItem('token'))
        },
        url: that.$config.apiUrl + 'canteenapp_orders/update_item_quantity',
        type: 'post',
        data: JSON.stringify({ item_id: item.id, quantity: newQty }),
        error: function () {
          // Revert on failure
          that.$set(order.items, itemIndex, Object.assign({}, item, { quantity: currentQty }))
          swal({ text: 'Could not update quantity. Please try again.', icon: 'error', button: 'Ok' })
        }
      })
    },
    saveNote: function(order){
      var that = this
      var postData ={
        order_id:order.id,
        note:that.note
      }
      $.showLoading()
      $.ajax({
        beforeSend: function (xhr) {
          xhr.setRequestHeader('Authorization', window.localStorage.getItem('token'))
        },
        url: that.$config.apiUrl +'/canteenapp_orders/update_order_note',
        type: 'post',
        data:JSON.stringify(postData),
        success: function (data) {
          if(data.success){
            that.reloadCalendar()
            swal({
              text: data.message,
              icon: 'success',
              button: 'Ok'
            })
          }else{
            swal({
              text: data.message,
              icon: 'info',
              button: 'Ok'
            })
          }
        },
        error: function (data) {
          $.hideLoading()
          if (window.localStorage.getItem(that.url)) {
            var offlineData = JSON.parse(window.localStorage.getItem(that.url))
            that.success(offlineData)
          } else {
            swal({
              text: data.responseJSON.message,
              icon: 'info',
              button: 'Ok'
            })
          }
        }
      })
    },
    reloadCalendar: function(){
      var that = this
      var user = JSON.parse(window.localStorage.getItem('user'))
      var merchantId = user.merchant.id
      var bedId = this.$route.params.bed
      var url = this.$config.apiUrl +'medicater/calendar/?merchant_id=' + merchantId + '&bed_id=' + bedId
      if(window.localStorage.getItem('free_issue') == "Y"){
        var ward = JSON.parse(window.localStorage.getItem('ward'))
        url = this.$config.apiUrl +'canteenapp_basic/calendar_free_issues?ward_id='+ward.ward_id+'&merchant_id=' + merchantId + '&bed_id=' + bedId
      }
      $.ajax({
        beforeSend: function (xhr) {
          xhr.setRequestHeader('Authorization', window.localStorage.getItem('token'))
        },
        url: url,
        type: 'get',
        success: function (data) {
          var periods = []
          if (data.success === true) {
            data.data.periods_new.forEach(function (period) {
              periods.push({
                id: period.id,
                title: period.name,
                open_time: period.period_open_time,
                close_time: period.period_close_time,
                cut_off: period.period_cut_off,
                served: "("+period.period_open_time+"-"+period.period_close_time+")"
              })
            })
          }
          that.periods = periods
          var orders = []
          data.data.calendar.forEach(function (day) {
            if (day.orders) {
              that.periods.forEach(function (period) {
                for (var key in day.orders[period.id]) {
                  orders.push(day.orders[period.id][key])
                }
              })
            }
          })
          that.orders = orders
        },
        error: function (data) {
          if (window.localStorage.getItem(this.url)) {
            var offlineData = JSON.parse(window.localStorage.getItem(this.url))
            this.success(offlineData)
          } else {
            swal({
              text: 'Please connect to the internet',
              icon: 'info',
              button: 'Ok'
            })
          }
        }
      })
    },
    editNote: function(order){
      this.order = order
      this.note = order.note
      $('#note-modal').modal('show');
    },
    periodTitle: function (period) {
      var titleParts = period.title.split('.')
      return titleParts[0] + '<br /><small>' + titleParts[1] + '</small>'
    }
  }
}
</script>

<style scoped>
/* ── Base ── */
.preorder-page {
  min-height: 100vh;
  background: #f0f4f8;
  padding-bottom: 40px;
}

/* ── Page header ── */
.page-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 30px 20px 20px;
  background: linear-gradient(135deg, #1a587b 0%, #0f3d5c 100%);
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 10;
  margin-top: 3.2rem;
  border-radius: 0;
  box-shadow: 0 8px 32px rgba(15, 61, 92, 0.35);
}

.back-btn {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s;
}
.back-btn:hover { background: rgba(255, 255, 255, 0.25); }

.header-info {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.page-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #fff;
  letter-spacing: 0.2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.page-subtitle {
  font-size: 0.73rem;
  color: rgba(255, 255, 255, 0.65);
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 5px;
}
.page-subtitle i { font-size: 0.65rem; }

/* ── Section pad ── */
.section-pad {
  padding: 66px 16px 0;
}
.section-pad + .section-pad {
  padding-top: 14px;
}

/* ── Quick nav ── */
.quick-nav {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  padding: 72px 16px 0;
}

.quick-nav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 12px 14px;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 600;
  text-decoration: none !important;
  border: none;
  cursor: pointer;
  transition: transform 0.1s, box-shadow 0.15s;
}
.quick-nav-btn:active { transform: scale(0.97); }

.quick-nav-btn--ghost {
  background: #fff;
  color: #1a587b !important;
  border: 1.5px solid #cbd5e1;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}
.quick-nav-btn--ghost:hover { background: #f8fafc; }

.quick-nav-btn--primary {
  background: linear-gradient(135deg, #f7534f 0%, #e0352f 100%);
  color: #fff !important;
  box-shadow: 0 4px 14px rgba(247, 83, 79, 0.35);
}
.quick-nav-btn--primary:hover { box-shadow: 0 6px 18px rgba(247, 83, 79, 0.45); }

/* ── Periods section ── */
.periods-section {
  padding: 20px 16px 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
}

@media (min-width: 768px) {
  .periods-section {
    grid-template-columns: 1fr 1fr;
  }
  .periods-section .section-label {
    grid-column: 1 / -1;
  }
}

.section-label {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: #64748b;
  margin: 0 0 4px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.section-label i { font-size: 0.72rem; }

/* ── Period card ── */
.period-card {
  background: #fff;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 2px 14px rgba(0, 0, 0, 0.07);
  border: 1px solid #e8edf2;
  transition: box-shadow 0.2s;
  display: flex;
  flex-direction: column;
}
.period-card:hover { box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1); }

.period-card--closed {
  opacity: 0.6;
  filter: grayscale(0.4);
}
.period-card--closed .period-header {
  background: linear-gradient(135deg, #64748b 0%, #475569 100%);
}

/* Period header band */
.period-header {
  background: linear-gradient(135deg, #1a587b 0%, #0f3d5c 100%);
  padding: 14px 16px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.period-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.period-name-group {
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
}

.period-icon-wrap {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  color: #fff;
  flex-shrink: 0;
}
.period-icon-wrap svg { display: block; }

.period-name {
  font-size: 0.95rem;
  font-weight: 700;
  color: #fff;
  letter-spacing: 0.2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.period-status-badge {
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  padding: 4px 9px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.badge--open {
  background: rgba(16, 185, 129, 0.25);
  color: #6ee7b7;
  border: 1px solid rgba(16, 185, 129, 0.35);
}

.badge--closed {
  background: rgba(239, 68, 68, 0.25);
  color: #fca5a5;
  border: 1px solid rgba(239, 68, 68, 0.35);
}

.period-meta {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.meta-chip {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.75);
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(255, 255, 255, 0.1);
  padding: 3px 8px;
  border-radius: 6px;
}

.meta-chip--muted {
  color: rgba(255, 255, 255, 0.45);
  background: rgba(255, 255, 255, 0.06);
}

/* ── Meal list ── */
.meal-list {
  padding: 4px 0;
}

/* ── Order status badge row ── */
.order-status-row {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 7px 16px 6px;
  border-bottom: 1px solid #f1f5f9;
}

.order-status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}

.order-status-label {
  font-size: 0.68rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.7px;
}

.order-status-hint {
  font-size: 0.7rem;
  font-weight: 500;
  flex: 1;
}

.order-status--pending .order-status-dot   { background: #f59e0b; }
.order-status--pending .order-status-label { color: #b45309; }
.order-status--pending                     { background: #fffbeb; }

.order-status--confirmed .order-status-dot  { background: #10b981; }
.order-status--confirmed .order-status-label{ color: #065f46; }
.order-status--confirmed .order-status-hint { color: #6b7280; }
.order-status--confirmed                    { background: #f0fdf4; }

/* ── Meal rows ── */
.meal-row {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  border-bottom: 1px solid #f1f5f9;
  gap: 10px;
}
.meal-row:last-child { border-bottom: none; }

.meal-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #1a587b;
  flex-shrink: 0;
  opacity: 0.5;
}

.meal-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #0f172a;
  flex: 1;
}

/* ── Inline quantity + remove ── */
.meal-row-controls {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.qty-control {
  display: flex;
  align-items: center;
  gap: 0;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  background: #f8fafc;
}

.qty-btn {
  width: 26px;
  height: 26px;
  border: none;
  background: none;
  color: #1a587b;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.12s;
  line-height: 1;
}
.qty-btn:hover:not(:disabled) { background: #e0f2fe; }
.qty-btn:disabled { color: #cbd5e1; cursor: not-allowed; }

.qty-value {
  min-width: 22px;
  text-align: center;
  font-size: 0.82rem;
  font-weight: 700;
  color: #0f172a;
  border-left: 1px solid #e2e8f0;
  border-right: 1px solid #e2e8f0;
  padding: 0 2px;
  line-height: 26px;
}

.qty-readonly {
  font-size: 0.75rem;
  font-weight: 600;
  color: #94a3b8;
  flex-shrink: 0;
}

.remove-btn {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #fee2e2;
  border: none;
  color: #ef4444;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.72rem;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s;
}
.remove-btn:hover { background: #fecaca; }

/* ── Note row ── */
.note-row {
  display: flex;
  align-items: center;
  padding: 9px 16px 11px;
  background: #f8fafc;
  border-top: 1px solid #f1f5f9;
  gap: 9px;
}

.note-icon-wrap {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: #e0f2fe;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  color: #0284c7;
  flex-shrink: 0;
}

.note-text {
  font-size: 0.78rem;
  color: #64748b;
  flex: 1;
  font-style: italic;
}

.edit-note-btn {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #e0f2fe;
  border: none;
  color: #0284c7;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.72rem;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s;
}
.edit-note-btn:hover { background: #bae6fd; }

/* ── Empty state ── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 20px 16px 16px;
  color: #94a3b8;
}
.empty-state i { font-size: 1.4rem; }
.empty-state span { font-size: 0.78rem; font-weight: 500; }

/* ── Add meal button ── */
.add-meal-btn {
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  -webkit-box-pack: center;
  -ms-flex-pack: center;
  justify-content: center;
  gap: 7px;
  width: 100%;
  max-width: 90%;
  padding: 13px 16px;
  background: #124564;
  border: none;
  border-top: none;
  color: #fff;
  font-size: 0.875rem;
  font-weight: 700;
  cursor: pointer;
  border-radius: 15px;
  margin-top: auto;
  margin-bottom: 1em;
  align-self: center;
  -webkit-transition: opacity 0.15s;
  transition: opacity 0.15s;
  letter-spacing: 0.2px;
}
.add-meal-btn:hover:not(:disabled) { opacity: 0.88; }

.add-meal-btn--disabled,
.add-meal-btn:disabled {
  background: #f8fafc;
  color: #94a3b8;
  border-top-color: #e2e8f0;
  cursor: not-allowed;
  border-top-style: solid;
}

/* ── Modal ── */
.modal-header {
  background: linear-gradient(135deg, #1a587b 0%, #0f3d5c 100%);
  border-bottom: none;
  border-radius: 0.3rem 0.3rem 0 0;
  padding: 16px 20px;
}

.modal-header .modal-title {
  color: #fff;
  font-weight: 700;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  gap: 8px;
}
.modal-header .close { color: #fff; opacity: 0.7; text-shadow: none; }
.modal-header .close:hover { opacity: 1; }

.note-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #475569;
  margin-bottom: 8px;
  display: block;
}

.note-textarea {
  min-height: 140px;
  border-radius: 10px;
  border: 1.5px solid #e2e8f0;
  font-size: 0.875rem;
  resize: vertical;
  color: #0f172a;
}
.note-textarea:focus {
  border-color: #1a587b;
  box-shadow: 0 0 0 3px rgba(26, 88, 123, 0.12);
  outline: none;
}

.modal-footer {
  border-top: 1px solid #f1f5f9;
  padding: 12px 16px;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.btn-cancel-note {
  padding: 9px 18px;
  border-radius: 9px;
  border: 1.5px solid #cbd5e1;
  background: #fff;
  color: #475569;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}
.btn-cancel-note:hover { background: #f8fafc; }

.btn-save-note {
  padding: 9px 18px;
  border-radius: 9px;
  background: linear-gradient(135deg, #1a587b 0%, #0f3d5c 100%);
  color: #fff;
  font-size: 0.85rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: opacity 0.15s;
  box-shadow: 0 3px 10px rgba(26, 88, 123, 0.3);
}
.btn-save-note:hover { opacity: 0.9; }
</style>
